import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { CustomClipEditor } from '../CustomClipping/CustomClipEditor';
import type { EditorMode } from '../CustomClipping/CustomClipEditor';
import { DeleteClipDialog } from '../CustomClipping/DeleteClipDialog';
import { TrackClipsModal } from './TrackClipsModal';
import { TrackRow } from './TrackRow';
import * as styles from './TrackClipTable.styles';
import {
  DEFAULT_COLUMNS,
  type ColumnKey,
  type TrackClipTableProps,
} from './types';

const HEADER_LABELS: Record<string, React.ReactNode> = {
  thumbnail: '',
  titleArtist: 'Title/Artist',
  album: 'Album',
  explicitness: 'Explicitness',
  dateAdded: 'Date Added',
  language: 'Language',
  popularity: 'Popularity',
  more: '',
};

/**
 * Clickable "Title/Artist" header. Both labels stay visible; the active sort
 * field is emphasized and carries a direction arrow. The click cycle itself is
 * owned by the consumer via onClick.
 */
const TitleArtistSortHeader: React.FC<{
  sortField?: 'title' | 'artist' | null;
  sortOrder?: 'asc' | 'desc';
  onClick: () => void;
}> = ({ sortField, sortOrder = 'asc', onClick }) => {
  const half = (active: boolean) => ({
    display: 'inline-flex',
    alignItems: 'center',
    fontWeight: active ? 700 : 'inherit',
    color: active ? 'primary.main' : 'inherit',
  });

  const ActiveArrow = sortOrder === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
  const inactive = sortField == null;

  return (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label="Sort by title or artist"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        ...(inactive
          ? {
              '&:hover': { color: 'text.primary' },
              '&:hover .ta-preview-arrow': { opacity: 0.5 },
            }
          : {}),
      }}
    >
      <Box component="span" sx={half(sortField === 'title')}>
        {sortField === 'title' && <ActiveArrow sx={{ fontSize: 16, mr: 0.25 }} />}
        {sortField == null && (
          <ArrowUpwardIcon
            className="ta-preview-arrow"
            sx={{ fontSize: 16, mr: 0.25, opacity: 0, transition: 'opacity 120ms ease-in-out' }}
          />
        )}
        Title
      </Box>
      <Box component="span" sx={{ mx: 0.5 }}>
        /
      </Box>
      <Box component="span" sx={half(sortField === 'artist')}>
        Artist
        {sortField === 'artist' && <ActiveArrow sx={{ fontSize: 16, ml: 0.25 }} />}
      </Box>
    </Box>
  );
};

export const TrackClipTable: React.FC<TrackClipTableProps> = ({
  tracks,
  onCreateCustomClip,
  onUpdateCustomClip,
  onDeleteCustomClip,
  columns = [...DEFAULT_COLUMNS],
  emptyState,
  isLoading = false,
  onPlayCuratedClip,
  onPlayCustomClip,
  isClipPlaying,
  onEditorPreviewPlay,
  onTitleArtistSort,
  titleArtistSortField,
  sortableColumns,
  activeSortColumn,
  sortOrder = 'asc',
  onSort,
  onFindSimilar,
  transport,
}) => {
  const [editorMode, setEditorMode] = useState<EditorMode>({ kind: 'closed' });
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    clipId: number;
    name: string;
    trackId: number;
  } | null>(null);
  const [moreTrackId, setMoreTrackId] = useState<number | null>(null);

  const moreTrack =
    moreTrackId != null
      ? tracks.find((t) => t.trackId === moreTrackId) ?? null
      : null;

  // Reopen the More… modal after the editor closes, on the refreshed clip list.
  const returnToModal = (trackId: number) => {
    setEditorMode({ kind: 'closed' });
    setMoreTrackId(trackId);
  };

  const handleCreateClick = (track: (typeof tracks)[number]) => {
    setErrorMessage(null);
    setMoreTrackId(null);
    setEditorMode({
      kind: 'create',
      trackId: track.trackId,
      trackTitle: track.trackTitle,
      trackDurationMs: track.trackDurationMs,
      artworkThumbnailUrl: track.artworkThumbnailUrl,
      audioUrl: track.audioUrl,
      curatedClipStartMs: track.curatedClip.startMs,
      curatedClipEndMs: track.curatedClip.endMs,
      proposedName: `${track.trackTitle} Custom Clip ${track.customClips.length + 1}`,
    });
  };

  const handleEditClick = (
    track: (typeof tracks)[number],
    clip: (typeof tracks)[number]['customClips'][number],
  ) => {
    setErrorMessage(null);
    setMoreTrackId(null);
    setEditorMode({
      kind: 'edit',
      trackId: track.trackId,
      trackTitle: track.trackTitle,
      trackDurationMs: track.trackDurationMs,
      artworkThumbnailUrl: track.artworkThumbnailUrl,
      audioUrl: track.audioUrl,
      clipId: clip.clipId,
      name: clip.name,
      startMs: clip.startMs,
      endMs: clip.endMs,
    });
  };

  const handleSaveCreate = async (args: {
    name: string;
    startMs: number;
    endMs: number;
  }) => {
    if (editorMode.kind !== 'create') return;
    const trackId = editorMode.trackId;
    try {
      setSaving(true);
      await onCreateCustomClip(trackId, args);
      returnToModal(trackId);
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Couldn't save clip. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (args: {
    clipId: number;
    name: string;
    startMs: number;
    endMs: number;
  }) => {
    if (editorMode.kind !== 'edit') return;
    const trackId = editorMode.trackId;
    try {
      setSaving(true);
      await onUpdateCustomClip(args.clipId, {
        name: args.name,
        startMs: args.startMs,
        endMs: args.endMs,
      });
      returnToModal(trackId);
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Couldn't save clip. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditor = () => {
    if (editorMode.kind === 'closed') return;
    returnToModal(editorMode.trackId);
  };

  const handleRequestDelete = (clipId: number) => {
    if (editorMode.kind !== 'edit') return;
    setDeleteTarget({ clipId, name: editorMode.name, trackId: editorMode.trackId });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const trackId = deleteTarget.trackId;
    try {
      setSaving(true);
      await onDeleteCustomClip(deleteTarget.clipId);
      setDeleteTarget(null);
      returnToModal(trackId);
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Couldn't delete clip. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const renderHeader = (key: ColumnKey): React.ReactNode => {
    if (key === 'titleArtist' && onTitleArtistSort) {
      return (
        <TitleArtistSortHeader
          sortField={titleArtistSortField}
          sortOrder={sortOrder}
          onClick={onTitleArtistSort}
        />
      );
    }
    if (onSort && sortableColumns?.includes(key)) {
      const active = activeSortColumn === key;
      return (
        <TableSortLabel
          active={active}
          direction={active ? sortOrder : 'asc'}
          onClick={() => onSort(key)}
          sx={{
            '&.Mui-active, &.Mui-active .MuiTableSortLabel-icon, &.Mui-active:hover': {
              color: 'primary.main',
              fontWeight: 700,
            },
            '&:not(.Mui-active):hover, &:not(.Mui-active):hover .MuiTableSortLabel-icon': {
              color: 'text.primary',
            },
          }}
        >
          {HEADER_LABELS[key]}
        </TableSortLabel>
      );
    }
    return HEADER_LABELS[key];
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tracks.length === 0) {
    return <>{emptyState ?? <Typography>No tracks.</Typography>}</>;
  }

  return (
    <>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((key) => (
                <TableCell key={key} sx={styles.headerCell}>
                  {renderHeader(key)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tracks.map((track) => (
              <TrackRow
                key={track.trackId}
                track={track}
                columns={columns}
                onMoreClick={() => setMoreTrackId(track.trackId)}
                onPlay={
                  onPlayCuratedClip
                    ? () => onPlayCuratedClip(track.trackId)
                    : undefined
                }
                isPlaying={isClipPlaying?.(track.curatedClip.clipId)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TrackClipsModal
        track={moreTrack}
        onClose={() => setMoreTrackId(null)}
        onCreateClip={() => {
          if (moreTrack) handleCreateClick(moreTrack);
        }}
        onEditClip={(clip) => {
          if (moreTrack) handleEditClick(moreTrack, clip);
        }}
        onPlayPrimary={
          onPlayCuratedClip && moreTrack
            ? () => onPlayCuratedClip(moreTrack.trackId)
            : undefined
        }
        onPlayCustom={onPlayCustomClip}
        isClipPlaying={isClipPlaying}
        onFindSimilar={
          onFindSimilar && moreTrack
            ? () => {
                onFindSimilar(moreTrack);
                setMoreTrackId(null);
              }
            : undefined
        }
        transport={transport}
      />

      <CustomClipEditor
        mode={editorMode}
        saving={saving}
        errorMessage={errorMessage}
        onCancel={handleCancelEditor}
        onSaveCreate={handleSaveCreate}
        onSaveEdit={handleSaveEdit}
        onRequestDelete={handleRequestDelete}
        onPreviewPlayStart={onEditorPreviewPlay}
      />

      <DeleteClipDialog
        open={deleteTarget !== null}
        clipName={deleteTarget?.name ?? ''}
        deleting={saving}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
